/**
 * legal-footer — the storefront's permanently-reachable legal links
 * (DE: Impressum · AGB · Datenschutz; EN via the catalog), mounted at the
 * foot of a page's own content column by every surface a buyer can land on
 * cold: landing.view.ts, and order-access.view.ts on each of its branches,
 * where the buyer arrives from a fulfillment email with no session and no
 * other route to the statutory documents.
 *
 * Each link renders a real, resolvable `/legal#<anchor>` href
 * (`legalAnchorHref`, legal.state.ts) — never `href="#"` — so it stays
 * crawlable and shareable even before any JS runs. A plain left click
 * (`isPlainLeftClick`) intercepts it to open the matching document in the
 * same overlay checkout-disclosures.view.ts and sign-consent.view.ts open
 * — `<legal-document>` for terms/privacy, `<legal-imprint>` for imprint.
 * A modified click (⌘/ctrl/shift/a non-primary button) falls through to the
 * real `/legal` navigation, so "open in new tab" keeps working, and a
 * direct hit on `/legal#<anchor>` renders the page and scrolls to it
 * (legal-page.view.ts's own scrollToHash).
 *
 * The imprint link is absent when `Workspace.legal` is incomplete
 * (`imprintAvailable`, legal.state.ts) — the same gate
 * legal-page.view.ts's own imprint section uses: a fabricated statutory
 * disclosure is worse than a withheld one. While the card's first read is
 * in flight the imprint's slot holds an invisible copy of the link's own
 * label, so the row never reflows when a complete card answers. Terms and
 * privacy always render:
 * every mount is a page-foot row of these same three links, so no surface
 * carries a second copy of them for this one to duplicate.
 *
 * The bar is one plate of the page's own ground tone — `<schmancy-surface>`
 * on `containerLowest`, flat and fully opaque: no `elevated`, so no blur.
 * `opacity="solid"` alone still leaves the tonal scale's own material at a
 * 94%-density color-mix — its translucent-glass ceiling, not a true opaque
 * fill — so a high-contrast poster sliding underneath still ghosts through
 * the plate. `[--glass-opacity:100%]` overrides that ceiling directly: the
 * same color-mix formula it drives collapses to the flat `--glass-base`
 * tone with no `transparent` component left to mix in, which is the one
 * way this material reaches true opacity. The plate spans whatever box its
 * mount hands it and lays out no column of its own — every mount stands the
 * bar in the page's own content column and the plate resolves that box, so
 * its edge lands on the same verticals as the content above it and never
 * reads as a wider strip laid under a narrower page. A mount whose host sits
 * outside that column carries the column's own classes rather than handing
 * this component a number.
 * Nothing on the bar is a drawn line: the plate's edge is the edge,
 * and the links are held apart by the space between them. The material is
 * the component's; WHERE the bar sits — sticky, and against which scroll
 * box — belongs to each mount, which is why the positioning classes stay
 * there and no `bg-*` does.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
// Side-effect: registers <schmancy-surface>, the bar's own ground plate. Named
// here rather than left to the app's auto-import plugin, whose scan is rooted
// at each app's own src and never reaches this package: undefined, the tag
// still lays its children out and paints nothing, so the bar would lose its
// ground silently on whichever route imports it from nowhere else.
import "@mhmo91/schmancy/surface";
import { css, html } from "lit";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { brandLoading, workspaceDomain } from "../workspace/workspace.state.ts";
import "./legal-document.view.ts";
import "./legal-imprint.view.ts";
import {
	imprintAvailable,
	imprintIdentity,
	isPlainLeftClick,
	legalAnchorHref,
	legalContentState,
	legalDocumentFor,
} from "./legal.state.ts";
import "../storefront/revia-logo.view.ts";

@schmancyElement("legal-footer")
export class LegalFooter extends LocalizeMixin(SchmancyElement) {
	// Block, not the inline default — a footer row belongs in the page's own
	// block flow, mirrors brand-bar.view.ts's own reasoning.
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	/** Opens `which`'s resolved document in the shared overlay — same
	 *  mechanism checkout-disclosures.view.ts and sign-consent.view.ts use. */
	private openDocument(which: "terms" | "privacy") {
		const { document, identityUnavailable } = legalDocumentFor(
			which,
			this.uiLanguage,
			workspaceDomain.get(),
			this.t("legal.identityUnavailable"),
		);
		show(
			() => html`
				<legal-document
					.document=${document}
					?identityUnavailable=${identityUnavailable}
				></legal-document>
			`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** One document link's markup — `which`'s own label, href, and overlay
	 *  open, set in the footer's shared label typography. */
	private documentLink(which: "terms" | "privacy") {
		return html`
			<schmancy-typography type="label" token="sm">
				<a
					href=${legalAnchorHref(which)}
					class="font-semibold text-primary-default hover:underline"
					@click=${(e: MouseEvent) => {
						if (!isPlainLeftClick(e)) return;
						e.preventDefault();
						this.openDocument(which);
					}}
					>${this.t(
						which === "terms" ? "legal.footer.terms" : "legal.footer.privacy",
					)}</a
				>
			</schmancy-typography>
		`;
	}

	override render() {
		const imprint = imprintAvailable.get();

		return html`
			<schmancy-surface
				type="containerLowest"
				opacity="solid"
				class="mx-auto w-full [--glass-opacity:100%]"
			>
				<footer class="flex items-end justify-between gap-3 pt-1 pb-2">
					<revia-logo></revia-logo>
					<div class="flex items-end gap-5">
						${when(
							imprint,
							() => html`
								<schmancy-typography type="label" token="sm">
									<a
										href=${legalAnchorHref("imprint")}
										class="font-semibold text-primary-default hover:underline"
										@click=${(e: MouseEvent) => {
											if (!isPlainLeftClick(e)) return;
											e.preventDefault();
											const legal = imprintIdentity.get();
											if (!legal) return;
											show(
												() =>
													html`<legal-imprint .legal=${legal}></legal-imprint>`,
											)
												.pipe(takeUntil(this.disconnecting))
												.subscribe();
										}}
										>${this.t("legal.footer.imprint")}</a
									>
								</schmancy-typography>
							`,
							() =>
								when(
									brandLoading.get(),
									() => html`
										<schmancy-typography
											type="label"
											token="sm"
											class="invisible font-semibold"
											aria-hidden="true"
											>${this.t("legal.footer.imprint")}</schmancy-typography
										>
									`,
								),
						)}
						${when(
							legalContentState.value.settled,
							() =>
								html`${this.documentLink("terms")}${this.documentLink("privacy")}`,
							() => html`
								<schmancy-visually-hidden role="status" aria-live="polite">${this.t("legal.loading")}</schmancy-visually-hidden>
								<schmancy-skeleton shape="text" class="w-10" aria-hidden="true"></schmancy-skeleton>
								<schmancy-skeleton shape="text" class="w-12" aria-hidden="true"></schmancy-skeleton>
							`,
						)}
					</div>
				</footer>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"legal-footer": LegalFooter;
	}
}
